import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {ChangeEvent, KeyboardEvent as ReactKeyboardEvent} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import type {
    BodyScrollEndEvent,
    ColDef,
    GetRowIdParams,
    ICellRendererParams,
    SortChangedEvent,
    SuppressHeaderKeyboardEventParams,
    SuppressKeyboardEventParams
} from 'ag-grid-community';
import {CircleQuestionMark, PencilIcon, Trash2Icon} from 'lucide-react';
import _ from 'lodash';
import {AppGrid} from 'components/common/ag-grid/AppGrid.component';
import {useAvailableHeight} from 'components/common/ag-grid/useAvailableHeight';
import {isLastRowRendered} from 'components/common/ag-grid/gridPaging';
import {Button, ButtonType} from 'components/common/Button.component';
import {ICON_SIZE} from 'components/common/constants';
import {Loader, LoaderSize} from 'components/common/Loader.component';
import {SegmentedControl} from 'components/common/SegmentedControl.component';
import {Tooltip} from 'components/tooltip/Tooltip';
import {HEADING_INFO_TOOLTIP_PLACE, SHARED_TOOLTIP_ID} from 'components/tooltip/AppTooltip.component';
import {useModalContext} from 'components/modals/ModalContext';
import {useTranslation} from 'components/translations/useTranslation';
import {launchDarkly} from 'utils/launchDarkly';
import {LOADING_STATUS, MODAL_ACTIONS, MODAL_SEVERITY} from 'utils/constants';
import {formatDate} from 'utils/valueFormatter.function';
import {formatNameWithEmail} from 'utils/userFormatter.function';
import type {Translate} from 'types/myTasks';
import {
    PersonField,
    STATUS_FILTER,
    StatusFilterValue,
    Substitute,
    SubstituteGridRow,
    SubstitutesSort
} from 'types/substitutes';
import * as reduxSelectors from '../store/application.reducers';
import SearchGroupInput from '../input/SearchGroupInput.component';
import Substitutes from './Substitutes.component';
import {
    changeActiveFilter,
    changeSearchValue,
    changeSort,
    deleteSubstitute,
    getSubstitutes
} from './substitutes.action';
import './newSubstitutes.scss';

// swaps the classic list in Substitutes.component for the ag-grid rendering below
const AG_GRID_IN_CONFIGURATION_FLAG = 'fe-use-ng-grid-in-configuration';

const TODAY = new Date().setHours(0, 0, 0, 0);
const NOT_ASSIGNED = 'N/A';

// what a date column shows when there is no date to show
const NO_DATE = '-';

const FIRST_PAGE = 1;
const SEARCH_DEBOUNCE_MS = 500;

// the name over its email, as tight as the two lines still fit in
const ROW_HEIGHT = 52;

// the grid fills the page, keeping the card's own padding and the page's bottom margin below it
const GRID_HEIGHT = Object.freeze({bottomGap: 48, watchSelector: '.new-substitutes__card'});

// the columns the grid draws, in the order the classic list had them
const USER_COL = 'user';
const SUBSTITUTE_COL = 'substitute';
const NOT_BEFORE_COL = 'notBefore';
const NOT_AFTER_COL = 'notAfter';
const STATUS_COL = 'status';
const ACTIONS_COL = 'actions';

// the header only ever flips between the two, never back to unsorted, as the classic list did
const SORTING_ORDER: ('asc' | 'desc')[] = ['asc', 'desc'];

// the tab order below is these two, in the order the page draws them
const SORTABLE_HEADERS = '.ag-header-cell-sortable';
const ACTION_BUTTONS = '.new-substitutes__actions button';

/**
 * Takes Tab off the grid in a cell that carries controls of its own.
 *
 * The grid answers Tab itself, by walking its cells - and with cell focus suppressed (see
 * AppGrid) it has no cell to walk to, so it takes focus out of the grid altogether. A button in
 * a cell would then be somewhere focus can land and never leave. Suppressed, the key is
 * onGridKeyDown's to answer instead.
 */
const tabIsNotTheGrids = ({event}: SuppressKeyboardEventParams<SubstituteGridRow>) => event.key === 'Tab';

/**
 * The same, for the heading Tab leaves the header row from. The grid walks its headings one by
 * one and then loops back to the first, which is a ring focus never gets out of; onGridKeyDown
 * carries it into the rows instead. Shift-Tab is left alone - walking back up the headings is
 * what it should do.
 */
const tabOutOfTheHeaderIsNotTheGrids = ({event}: SuppressHeaderKeyboardEventParams<SubstituteGridRow>) =>
    event.key === 'Tab' && !event.shiftKey;

/** Nothing before a start date, and active or planned once there is one. */
function computeStatus(fromDate: number | null, translate: Translate): string {
    if (fromDate === null) {
        return '';
    }
    return fromDate <= TODAY ? translate('substitutes.active') : translate('substitutes.planned');
}

function buildRowData(substitutes: Substitute[], translate: Translate): SubstituteGridRow[] {
    return substitutes.map((substitute, index) => {
        const fromDate = substitute.from ? new Date(substitute.from).setHours(0, 0, 0, 0) : null;
        const toDate = substitute.to ? new Date(substitute.to).setHours(0, 0, 0, 0) : null;

        return {
            // the same row can be listed twice over different periods, so the pair of people is
            // not unique on its own: the version and the position are what tell those rows apart
            id: `${substitute.userId}-${substitute.substituteId}-${substitute.version}-${index}`,
            index,
            user: substitute.userName,
            substitute: substitute.substituteName === NOT_ASSIGNED ? '' : substitute.substituteName,
            notBefore: formatDate(fromDate, NO_DATE),
            notAfter: toDate
                ? formatDate(toDate, NO_DATE)
                : (substitute.from ? translate('substitutes.indefinitely') : NO_DATE),
            status: computeStatus(fromDate, translate)
        };
    });
}

/**
 * The name on the first line, the email under it - as the classic list rendered both columns.
 *
 * A div, not a span: formatNameWithEmail returns a div of its own once the value carries an
 * email, and a div inside a span is markup no browser has to lay out the way it reads.
 */
const personRenderer = (field: PersonField) => (params: ICellRendererParams<SubstituteGridRow>) => (
    <div className="new-substitutes__person">{formatNameWithEmail(params.data?.[field])}</div>
);

/**
 * ag-grid rendering of the Configuration -> Substitutes page, behind
 * fe-use-ng-grid-in-configuration. The classic list it replaces is in Substitutes.component.
 */
export const NewSubstitutes = () => {

    const {translate} = useTranslation();
    const dispatch = useDispatch();
    // every popup this page opens is the modal router's
    const toggleModal = useModalContext()?.toggleModal;

    const currentContextId = useSelector(reduxSelectors.getUsersCurrentContextId);
    const userRoles = useSelector(reduxSelectors.getUsersRoles);
    const activeFilter: StatusFilterValue = useSelector(reduxSelectors.getSubstitutesActiveFilter);
    const searchValue: string = useSelector(reduxSelectors.getSubstitutesSearchValue);
    const substitutes = useSelector(reduxSelectors.getSubstitutesForRender);
    const sort: SubstitutesSort = useSelector(reduxSelectors.getSubstitutesSorting);
    const loadingStatus = useSelector(reduxSelectors.getSubstitutesLoadingStatus);

    // typed text lives here so keystrokes never reach redux or the API
    const [searchInput, setSearchInput] = useState<string>(searchValue || '');

    // the loader stands in the grid's place until the first page lands, and the two are
    // measured as one box, so the card keeps its height when the rows arrive. Whichever of
    // them is mounted is the element measured, which is why it arrives through a callback
    const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null);
    const gridHeight = useAvailableHeight(gridElement, GRID_HEIGHT);

    const allSubstitutes: Substitute[] = substitutes?.allSubstitutes ?? [];
    const showActions = !!userRoles?.systemAdministrator;

    /**
     * The loaded rows, for the handlers that only look one up when they run. Reading them off a
     * ref rather than closing over them is what keeps those handlers - and with them the column
     * definitions - the same objects across a reload, so the grid does not rebuild its columns
     * (and drop a resized width) every time a page lands.
     */
    const loadedSubstitutes = useRef(allSubstitutes);
    loadedSubstitutes.current = allSubstitutes;

    /**
     * The query a debounced search should run with. The debounced call is created once, so it
     * cannot close over the filter and the sort - it reads whatever they are when it fires.
     */
    const latestQuery = useRef({activeFilter, sort});
    latestQuery.current = {activeFilter, sort};

    const debouncedSearch = useMemo(() => _.debounce((value: string) => {
        dispatch(changeSearchValue(value));
        dispatch(getSubstitutes(FIRST_PAGE, latestQuery.current.activeFilter, value, latestQuery.current.sort, true));
    }, SEARCH_DEBOUNCE_MS), [dispatch]);

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    // loads on mount, and starts over whenever the company context changes
    useEffect(() => {
        dispatch(getSubstitutes(FIRST_PAGE, activeFilter, searchValue, sort));
    }, [dispatch, currentContextId]); // eslint-disable-line react-hooks/exhaustive-deps -- the filter, the search term and the sort reload through their own handlers

    /**
     * Holds the paging back until the page asked for has landed. The classic list re-armed this
     * whenever it rebuilt its rows, and a render is still what re-arms it: the grid scrolling
     * does not re-render the page, so the guard stands from the request until its response.
     */
    const stopScroll = useRef(false);
    useEffect(() => {
        stopScroll.current = false;
    });

    /** Drops the pending keystroke fetch and commits whatever is typed right now. */
    const commitSearch = useCallback(() => {
        debouncedSearch.cancel();
        if (searchInput !== searchValue) {
            dispatch(changeSearchValue(searchInput));
        }
        return searchInput;
    }, [debouncedSearch, dispatch, searchInput, searchValue]);

    const search = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setSearchInput(event.target.value);
        debouncedSearch(event.target.value);
    }, [debouncedSearch]);

    const clearSearch = useCallback(() => {
        debouncedSearch.cancel();
        setSearchInput('');
        dispatch(changeSearchValue(''));
        dispatch(getSubstitutes(FIRST_PAGE, activeFilter, '', sort, true));
    }, [debouncedSearch, dispatch, activeFilter, sort]);

    const filter = useCallback((value: StatusFilterValue) => {
        const committed = commitSearch();
        dispatch(changeActiveFilter(value));
        dispatch(getSubstitutes(FIRST_PAGE, value, committed, sort));
    }, [commitSearch, dispatch, sort]);

    const resetFilterToAll = useCallback(() => {
        dispatch(changeActiveFilter(STATUS_FILTER.ALL));
    }, [dispatch]);

    const closeSubstitutePopUp = useCallback(() => {
        debouncedSearch.cancel();
        setSearchInput('');
        dispatch(changeSearchValue(''));
        dispatch(getSubstitutes(FIRST_PAGE, activeFilter, '', sort, true));
    }, [debouncedSearch, dispatch, activeFilter, sort]);

    const deleteRow = useCallback((index: number) => {
        const row = loadedSubstitutes.current[index];
        toggleModal?.(MODAL_ACTIONS.CONFIRM_ACTION, {
            title: translate('popUp.deleteSubstitute.title'),
            description: translate('substitutes.confirmDelete', row.substituteName, row.userName),
            confirmButtonText: 'popUp.remove',
            cancelButtonText: 'popUp.cancel',
            severity: MODAL_SEVERITY.WARNING,
            handleAction: () => {
                toggleModal?.(null);
                // deleteSubstitute refetches the list once the delete resolves
                dispatch(deleteSubstitute(index));
            }
        });
    }, [toggleModal, translate, dispatch]);

    const editRow = useCallback((index: number) => {
        toggleModal?.(MODAL_ACTIONS.EDIT_SUBSTITUTE, {
            edit: loadedSubstitutes.current[index],
            saveCloseCallback: resetFilterToAll,
            closeCallback: closeSubstitutePopUp
        });
    }, [toggleModal, resetFilterToAll, closeSubstitutePopUp]);

    const openAddSubstitutePopUp = useCallback(() => {
        toggleModal?.(MODAL_ACTIONS.ADD_SUBSTITUTE, {
            saveCloseCallback: resetFilterToAll,
            closeCallback: closeSubstitutePopUp
        });
    }, [toggleModal, resetFilterToAll, closeSubstitutePopUp]);

    /**
     * The next page is asked for once the grid is scrolled to its last row. ag-grid virtualises
     * the rows, so there is no scrolling element of our own to watch for the bottom.
     */
    const onBodyScrollEnd = useCallback((event: BodyScrollEndEvent<SubstituteGridRow>) => {
        if (!isLastRowRendered(event.api) || stopScroll.current) {
            return;
        }
        const {records, page} = substitutes;
        if (allSubstitutes.length > 0 && allSubstitutes.length < records) {
            stopScroll.current = true;
            dispatch(getSubstitutes(page + 1, activeFilter, searchValue, sort, true));
        }
    }, [dispatch, substitutes, allSubstitutes.length, activeFilter, searchValue, sort]);

    /**
     * The endpoint sorts, not the grid, so a header click reloads the first page. The grid keeps
     * the arrow, and redux keeps the field it stands for - the column defs below hand it back,
     * so the two never drift apart.
     */
    const onSortChanged = useCallback((event: SortChangedEvent<SubstituteGridRow>) => {
        const sorted = event.api.getColumnState().find(column => !!column.sort);
        const newSort: SubstitutesSort = sorted
            ? {fieldName: sorted.colId, sortOrder: sorted.sort === 'asc'}
            : {fieldName: '', sortOrder: true};

        if (newSort.fieldName === sort.fieldName && newSort.sortOrder === sort.sortOrder) {
            // the grid was only handed back the sort it already had
            return;
        }

        const committed = commitSearch();
        dispatch(changeSort(newSort));
        dispatch(getSubstitutes(FIRST_PAGE, activeFilter, committed, newSort, true));
    }, [commitSearch, dispatch, activeFilter, sort]);

    /**
     * The tab order inside the grid: the sortable headings one by one, and then the buttons in
     * the rows, two to a row.
     *
     * What the browser would do on its own is walk every heading, sortable or not, and stop on
     * each row before the buttons it carries - the row is a tab stop of the grid's own making
     * (see AppGrid), and standing on one does nothing. Every step below is one of those skipped:
     * the last sortable heading hands focus to the first button, and the last button of a row
     * hands it to the first button of the row under it. The last button of the last rendered row
     * is left to the browser, which takes focus out of the grid - where it should go.
     */
    const onGridKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Tab') {
            return;
        }

        const grid = event.currentTarget;
        const buttons = Array.from(grid.querySelectorAll<HTMLElement>(ACTION_BUTTONS));
        if (buttons.length === 0) {
            return;
        }

        const headers = Array.from(grid.querySelectorAll<HTMLElement>(SORTABLE_HEADERS));
        const lastHeader = headers[headers.length - 1];
        const target = event.target as HTMLElement;

        const moveTo = (element: HTMLElement | undefined) => {
            if (!element) {
                return;
            }
            element.focus();
            event.preventDefault();
        };

        if (target === lastHeader && !event.shiftKey) {
            moveTo(buttons[0]);
            return;
        }

        const index = buttons.indexOf(target);
        if (index === -1) {
            return;
        }

        if (event.shiftKey) {
            // back off the first button, and the headings pick the walk up again
            moveTo(index === 0 ? lastHeader : buttons[index - 1]);
        } else if (index < buttons.length - 1) {
            moveTo(buttons[index + 1]);
        }
    }, []);

    const rowData = useMemo(() => buildRowData(allSubstitutes, translate), [allSubstitutes, translate]);

    const columnDefs = useMemo<ColDef<SubstituteGridRow>[]>(() => {
        const sortFor = (colId: string) => sort.fieldName === colId
            ? (sort.sortOrder ? 'asc' as const : 'desc' as const)
            : null;

        const sortableColumn = (colId: string): Partial<ColDef<SubstituteGridRow>> => ({
            sortable: true,
            sortingOrder: SORTING_ORDER,
            sort: sortFor(colId),
            // the endpoint has already ordered the rows, and only the loaded pages are here
            // anyway - so the grid draws the arrow and leaves the order alone
            comparator: () => 0
        });

        const column = (colId: string, headerKey: string, flex: number, extra: Partial<ColDef<SubstituteGridRow>> = {}): ColDef<SubstituteGridRow> => {
            const label = translate(headerKey);
            return {
                colId,
                field: colId as keyof SubstituteGridRow,
                headerName: label,
                // a narrowed column clips its heading; the tooltip is what spells it out again
                headerTooltip: label,
                flex,
                minWidth: 110,
                sortable: false,
                ...extra
            };
        };

        const personColumn = (colId: PersonField, headerKey: string): ColDef<SubstituteGridRow> =>
            column(colId, headerKey, 3, {
                ...sortableColumn(colId),
                minWidth: 160,
                cellRenderer: personRenderer(colId),
                // ag-grid cannot spot truncation inside a cell renderer, so the tooltip is asked
                // for explicitly - it carries both lines, since either one can be cut
                tooltipValueGetter: params => params.data?.[colId] || undefined
            });

        // the flex weights are the proportions the classic list gave these columns with
        // col-md-3 / col-md-3 / col-md-2 / col-md-2 / col-md-1
        const columns: ColDef<SubstituteGridRow>[] = [
            personColumn(USER_COL, 'substitutes.headers.user'),
            personColumn(SUBSTITUTE_COL, 'substitutes.headers.substitute'),
            column(NOT_BEFORE_COL, 'substitutes.headers.notBefore', 2, sortableColumn(NOT_BEFORE_COL)),
            column(NOT_AFTER_COL, 'substitutes.headers.notAfter', 2, sortableColumn(NOT_AFTER_COL)),
            column(STATUS_COL, 'substitutes.headers.status', 1)
        ];

        if (showActions) {
            const editLabel = translate('workflows.edit');
            const deleteLabel = translate('workflows.delete');

            columns.push({
                colId: ACTIONS_COL,
                headerName: translate('substitutes.headers.action'),
                type: 'rightAligned',
                width: 110,
                minWidth: 110,
                maxWidth: 110,
                sortable: false,
                resizable: false,
                // the buttons are the tab stops here, and onGridKeyDown is what walks them
                suppressKeyboardEvent: tabIsNotTheGrids,
                cellRenderer: (params: ICellRendererParams<SubstituteGridRow>) => {
                    const index = params.data?.index;
                    if (index === undefined) return null;
                    return (
                        <span className="new-substitutes__actions">
                            <Button type={ButtonType.GHOST}
                                    iconOnly={true}
                                    icon={<PencilIcon size={ICON_SIZE.MEDIUM}/>}
                                    onClick={() => editRow(index)}
                                    ariaLabel={editLabel}
                                    tooltip={{id: `substitutes-edit-tooltip-${index}`, content: () => editLabel}}
                            />
                            <Button type={ButtonType.GHOST}
                                    iconOnly={true}
                                    icon={<Trash2Icon size={ICON_SIZE.MEDIUM}/>}
                                    onClick={() => deleteRow(index)}
                                    ariaLabel={deleteLabel}
                                    tooltip={{id: `substitutes-delete-tooltip-${index}`, content: () => deleteLabel}}
                            />
                        </span>
                    );
                }
            });
        }

        // the heading Tab leaves the header row from - the last one it has anything to do at
        const lastSortable = _.findLast(columns, ({sortable}) => !!sortable);
        if (lastSortable) {
            lastSortable.suppressHeaderKeyboardEvent = tabOutOfTheHeaderIsNotTheGrids;
        }

        return columns;
    }, [translate, showActions, sort.fieldName, sort.sortOrder, editRow, deleteRow]);

    // a reload keeps the rows on screen; only the very first page is waited for
    const showGrid = loadingStatus !== LOADING_STATUS.LOADING || rowData.length > 0;

    return (
        // the outer wrapper is what ConfigurationWrapper used to render around every configuration page
        <div>
            <div className="w-100">
                <main className="page w-100 d-flex justify-content-center align-items-start">
                    <div className="py-16 mb-48 w-75 settings-page__container">
                        <h2 className="ps-4 mb-4 d-flex align-items-center">{translate("substitutes.header")}
                            <span className="ms-4"
                                  data-for={SHARED_TOOLTIP_ID}
                                  data-place={HEADING_INFO_TOOLTIP_PLACE}
                                  data-tip="substitutes-header-info-tooltip">
                                <CircleQuestionMark size={ICON_SIZE.MEDIUM}/>
                            </span>
                            <Tooltip
                                id="substitutes-header-info-tooltip"
                                content={() => translate("substitutes.headerInfo")}
                            />
                        </h2>

                        <div className="bg-white radius-4 p-4 setting_collapsable-main new-substitutes__card">
                            <div className="d-flex justify-content-between mb-24">
                                <div>
                                    {showActions &&
                                        <div className="btn-group">
                                            <Button type={ButtonType.PRIMARY}
                                                    onClick={openAddSubstitutePopUp}
                                                    text={translate("substitutes.newSubstitute")}
                                            />
                                        </div>
                                    }
                                </div>

                                <div className="d-flex gap-3 align-items-center">
                                    <SearchGroupInput onInputChange={search}
                                                      placeholder={translate("popUp.addSubstitute.name")}
                                                      value={searchInput}
                                                      className="ga-input__default"
                                                      onClearInput={clearSearch}
                                                      translate={translate}/>

                                    <SegmentedControl
                                        options={[
                                            {
                                                key: STATUS_FILTER.ALL,
                                                label: translate("substitutes.all"),
                                                isSelected: activeFilter === STATUS_FILTER.ALL,
                                                onClick: () => filter(STATUS_FILTER.ALL)
                                            },
                                            {
                                                key: STATUS_FILTER.CURRENT,
                                                label: translate("substitutes.active"),
                                                isSelected: activeFilter === STATUS_FILTER.CURRENT,
                                                onClick: () => filter(STATUS_FILTER.CURRENT)
                                            },
                                            {
                                                key: STATUS_FILTER.UPCOMING,
                                                label: translate("substitutes.planned"),
                                                isSelected: activeFilter === STATUS_FILTER.UPCOMING,
                                                onClick: () => filter(STATUS_FILTER.UPCOMING)
                                            }
                                        ]}
                                    />
                                </div>
                            </div>

                            {showGrid
                                ? <AppGrid<SubstituteGridRow>
                                    className="new-substitutes__grid"
                                    dataCy="substitutes.grid"
                                    containerRef={setGridElement}
                                    height={gridHeight}
                                    rowData={rowData}
                                    columnDefs={columnDefs}
                                    getRowId={(params: GetRowIdParams<SubstituteGridRow>) => params.data.id}
                                    rowHeight={ROW_HEIGHT}
                                    onBodyScrollEnd={onBodyScrollEnd}
                                    onSortChanged={onSortChanged}
                                    onKeyDown={onGridKeyDown}
                                    // one field is all the endpoint sorts by, and all
                                    // onSortChanged reads back - so a shift-click must not be
                                    // able to leave two columns wearing an arrow
                                    suppressMultiSort={true}
                                    localeText={{noRowsToShow: translate('substitutes.noMatches')}}
                                />
                                : <div className="new-substitutes__loading" ref={setGridElement}
                                       style={gridHeight ? {height: gridHeight} : undefined}>
                                    <Loader size={LoaderSize.Medium}/>
                                </div>
                            }
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

/**
 * Configuration -> Substitutes. The flag is read here rather than around the route, so the page
 * itself decides which of its two renderings it is.
 */
export const SubstitutesWrapper = () => {
    const useAgGrid = launchDarkly.userClient?.variation(AG_GRID_IN_CONFIGURATION_FLAG, false) ?? false;

    return useAgGrid ? <NewSubstitutes/> : <Substitutes/>;
};

export default SubstitutesWrapper;
